import { useState, useEffect } from "react";
import api from "../../services/api";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart,
} from "recharts";

// Même garde-fou que Budget.jsx (onglets Scénarios/Comparaison) : le claim
// JWT `plan` n'existe plus dans aucun token émis depuis le passage au modèle
// socle + modules, donc planRank retombe systématiquement à 0 pour tout le
// monde. Tant que SAAS_MODE est désactivé (pas de REACT_APP_SAAS_MODE=true),
// ce verrou ne doit pas masquer le panneau.
const SAAS_MODE = process.env.REACT_APP_SAAS_MODE === 'true';

function KPI({ label, budget, realise, icon, inverted }) {
  const pct   = budget > 0 ? Math.round((realise / budget) * 100) : 0;
  const good  = inverted ? realise <= budget : realise >= budget;
  const color = good ? "#16a34a" : "#dc2626";
  const fmt   = (n) => (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";

  return (
    <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: "16px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}</div>
        <span style={{ fontSize: 22 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text,#1f2937)", marginBottom: 4 }}>{fmt(realise)}</div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>Budget : {fmt(budget)}</div>
      <div style={{ background: "#f3f4f6", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, transition: "width .5s" }} />
      </div>
      <div style={{ fontSize: 11, color, marginTop: 4, fontWeight: 600 }}>{pct}% réalisé</div>
    </div>
  );
}

export default function DashboardTab({ scenarioId, planRank }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [aiText, setAiText]     = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showAI, setShowAI]     = useState(false);
  const [breakeven, setBreakeven] = useState(null);

  useEffect(() => {
    if (!scenarioId) return;
    setLoading(true);
    api.budget.dashboard(scenarioId).then(setData).catch(console.error).finally(() => setLoading(false));
    api.budget.breakeven(scenarioId).then(setBreakeven).catch(() => {});
  }, [scenarioId]);

  const runAI = async () => {
    setShowAI(true);
    setAiLoading(true);
    try {
      const r = await api.budget.aiAnalysis(scenarioId);
      setAiText(r.analysis || "");
    } catch (e) { setAiText("Erreur : " + e.message); }
    finally { setAiLoading(false); }
  };

  if (!scenarioId) return (
    <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>
      <div style={{ fontSize: 13 }}>Sélectionnez ou créez un scénario pour voir le tableau de bord</div>
    </div>
  );

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement...</div>;
  if (!data) return null;

  const { kpis, monthlyData = [], ecartsByLine = [], alertLines = [] } = data;
  const fmt = (n) => (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";

  return (
    <div style={{ display: "flex", gap: 20 }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
          <KPI label="Chiffre d'affaires" budget={kpis.caBudget} realise={kpis.caRealise} icon="📈" inverted={false} />
          <KPI label="Charges totales" budget={kpis.chargesBudget} realise={kpis.chargesReal} icon="💸" inverted={true} />
          <KPI label="Résultat net" budget={kpis.resultatBudget} realise={kpis.resultatRealise} icon="💰" inverted={false} />
          {breakeven && (
            <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Point mort (CA min.)</div>
                <span style={{ fontSize: 22 }}>⚖️</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text,#1f2937)", marginBottom: 4 }}>
                {breakeven.breakEvenCA.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
                Marge contrib. : {(breakeven.margeContrib * 100).toFixed(1)}%
              </div>
              <div style={{ background: "#f3f4f6", borderRadius: 4, height: 6, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(breakeven.pctAtteinte, 100)}%`, background: breakeven.pctAtteinte >= 100 ? "#16a34a" : breakeven.pctAtteinte >= 80 ? "#f59e0b" : "#ef4444", borderRadius: 4 }} />
              </div>
              <div style={{ fontSize: 11, color: breakeven.pctAtteinte >= 100 ? "#16a34a" : "#dc2626", marginTop: 4, fontWeight: 600 }}>
                {breakeven.pctAtteinte}% du seuil budgété
              </div>
            </div>
          )}
        </div>

        {/* Budget vs Réalisé monthly */}
        <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Évolution mensuelle — Budget vs Réalisé</div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, n) => [`${v.toLocaleString("fr-FR")} €`, n]} />
              <Legend />
              <Bar dataKey="realise" name="Réalisé" fill="#3b82f6" opacity={0.85} radius={[3,3,0,0]} />
              <Line type="monotone" dataKey="budget" name="Budget" stroke="#f59e0b" strokeWidth={2} dot={false} strokeDasharray="6 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Écarts par poste */}
        {ecartsByLine.length > 0 && (
          <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Principaux écarts par poste (€)</div>
            <ResponsiveContainer width="100%" height={Math.max(160, ecartsByLine.slice(0,10).length * 28)}>
              <BarChart layout="vertical" data={ecartsByLine.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="account_name" width={160} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${v.toLocaleString("fr-FR")} €`, "Écart"]} />
                <Bar dataKey="ecart" name="Écart" radius={[0,3,3,0]}
                  fill="#22c55e"
                  label={false}
                >
                  {ecartsByLine.slice(0, 10).map((e, i) => (
                    <rect key={i} fill={e.ecart < 0 && e.category === 'produit' ? "#ef4444" : e.ecart > 0 && e.category === 'charge' ? "#ef4444" : "#22c55e"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Right panel — alerts + AI */}
      <div style={{ width: 300, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Alerts */}
        <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            Alertes budgétaires
            {alertLines.length > 0 && <span style={{ background: "#ef4444", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>{alertLines.length}</span>}
          </div>
          {alertLines.length === 0 ? (
            <div style={{ fontSize: 12, color: "#16a34a" }}>✓ Aucune alerte — tout est dans les clous</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {alertLines.slice(0, 8).map(a => (
                <div key={a.account_id} style={{ padding: "8px 10px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#dc2626" }}>{a.account_name}</div>
                  <div style={{ fontSize: 10, color: "#7f1d1d", marginTop: 2 }}>
                    {a.category === "charge" ? "Dépassement" : "Sous-réalisation"} : {a.pct > 0 ? "+" : ""}{a.pct}%
                    ({a.ecart > 0 ? "+" : ""}{a.ecart.toLocaleString("fr-FR")} €)
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI panel */}
        {(planRank >= 2 || !SAAS_MODE) && (
          <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Analyse IA</div>
            {!showAI ? (
              <div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
                  Obtenez une analyse experte des écarts et des recommandations d'actions correctives.
                </div>
                <button onClick={runAI} style={{ width: "100%", padding: "9px 14px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  Lancer l'analyse IA
                </button>
              </div>
            ) : (
              <div>
                {aiLoading ? (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-muted)", fontSize: 12 }}>Analyse en cours...</div>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: "auto" }}>
                    <div style={{ fontSize: 11, lineHeight: 1.7, whiteSpace: "pre-wrap", color: "var(--text,#1f2937)" }}>{aiText}</div>
                    <button onClick={() => setShowAI(false)} style={{ marginTop: 12, fontSize: 11, color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer" }}>Fermer</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary table */}
        <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Résumé annuel</div>
          {[
            { label: "CA budgété", val: fmt(kpis.caBudget), color: "#1f2937" },
            { label: "CA réalisé", val: fmt(kpis.caRealise), color: "#3b82f6" },
            { label: "Charges budget", val: fmt(kpis.chargesBudget), color: "#1f2937" },
            { label: "Charges réel.", val: fmt(kpis.chargesReal), color: "#8b5cf6" },
            { label: "Résultat budget", val: fmt(kpis.resultatBudget), color: "#1f2937" },
            { label: "Résultat réel", val: fmt(kpis.resultatRealise), color: kpis.resultatRealise >= 0 ? "#16a34a" : "#dc2626" },
          ].map(r => (
            <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f3f4f6", fontSize: 12 }}>
              <span style={{ color: "var(--text-muted)" }}>{r.label}</span>
              <span style={{ fontWeight: 600, color: r.color }}>{r.val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
